import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PerformanceCharts = ({ timeRange, refreshTrigger }) => {
  const [activeChart, setActiveChart] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingTrendsData, setBookingTrendsData] = useState([]);
  const [driverUtilizationData, setDriverUtilizationData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [geographicData, setGeographicData] = useState([]);
  const [satisfactionData] = useState([
    { name: 'Excellent', value: 68, color: '#38a169' },
    { name: 'Good', value: 23, color: '#3182ce' },
    { name: 'Average', value: 7, color: '#d69e2e' },
    { name: 'Poor', value: 2, color: '#e53e3e' },
  ]);
  const [summaryStats, setSummaryStats] = useState({ totalBookings: 0, peakHour: 'N/A', growthRate: 0 });

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      let startDate;
      if (timeRange === '1h') {
        startDate = new Date(now.getTime() - 60 * 60 * 1000)?.toISOString();
      } else if (timeRange === '24h') {
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)?.toISOString();
      } else if (timeRange === '7d') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      } else {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
      }

      // Fetch orders for booking trends
      const { data: orders, error: ordErr } = await supabase?.from('orders')?.select('id, service_type, created_at, total_amount, pickup_address, status')?.gte('created_at', startDate)?.order('created_at', { ascending: true });
      if (ordErr) throw ordErr;

      // Fetch driver utilization
      const { data: drivers, error: drvErr } = await supabase?.from('driver_profiles')?.select('status, created_at');
      if (drvErr) throw drvErr;

      // Fetch wallet_transactions for revenue
      const { data: walletTxns, error: wtErr } = await supabase?.from('wallet_transactions')?.select('amount, category, created_at, status')?.gte('created_at', startDate)?.eq('status', 'completed');
      if (wtErr) throw wtErr;

      // Build booking trends by hour (for 24h) or by day (for 7d/30d)
      const trendMap = {};
      const isHourly = timeRange === '24h' || timeRange === '1h';

      orders?.forEach(order => {
        const d = new Date(order?.created_at);
        const key = isHourly
          ? `${String(d?.getHours())?.padStart(2, '0')}:00`
          : d?.toLocaleDateString('en-US', { weekday: 'short' });
        if (!trendMap?.[key]) trendMap[key] = { name: key, rides: 0, deliveries: 0, total: 0 };
        if (order?.service_type === 'ride') trendMap[key].rides++;
        else trendMap[key].deliveries++;
        trendMap[key].total++;
      });

      const trendArr = Object.values(trendMap)?.slice(-8);
      setBookingTrendsData(trendArr);

      // Summary stats
      const totalBookings = orders?.length || 0;
      let peakHour = 'N/A';
      if (trendArr?.length > 0) {
        const peak = trendArr?.reduce((max, item) => item?.total > max?.total ? item : max, trendArr?.[0]);
        peakHour = peak?.name;
      }
      setSummaryStats({ totalBookings, peakHour, growthRate: totalBookings > 0 ? '+Live' : '0' });

      // Driver utilization by day of week
      const dayMap = { Mon: { name: 'Mon', available: 0, active: 0 }, Tue: { name: 'Tue', available: 0, active: 0 }, Wed: { name: 'Wed', available: 0, active: 0 }, Thu: { name: 'Thu', available: 0, active: 0 }, Fri: { name: 'Fri', available: 0, active: 0 }, Sat: { name: 'Sat', available: 0, active: 0 }, Sun: { name: 'Sun', available: 0, active: 0 } };
      const totalDrivers = drivers?.length || 0;
      const onlineDrivers = drivers?.filter(d => d?.status === 'online')?.length || 0;
      const busyDrivers = drivers?.filter(d => d?.status === 'busy')?.length || 0;
      // Spread current driver stats across days for visualization
      Object.keys(dayMap)?.forEach(day => {
        dayMap[day].available = totalDrivers;
        dayMap[day].active = onlineDrivers + busyDrivers;
        dayMap[day].utilization = totalDrivers > 0 ? Math.round(((onlineDrivers + busyDrivers) / totalDrivers) * 100) : 0;
      });
      setDriverUtilizationData(Object.values(dayMap));

      // Revenue breakdown by week
      const weekMap = {};
      walletTxns?.forEach(t => {
        const d = new Date(t?.created_at);
        const weekNum = Math.floor((now - d) / (7 * 24 * 60 * 60 * 1000));
        const key = weekNum === 0 ? 'This Week' : weekNum === 1 ? 'Last Week' : `${weekNum}w ago`;
        if (!weekMap?.[key]) weekMap[key] = { name: key, rides: 0, deliveries: 0, commissions: 0 };
        const amt = parseFloat(t?.amount || 0);
        if (t?.category === 'ride_payment') weekMap[key].rides += amt;
        else if (t?.category === 'delivery_payment') weekMap[key].deliveries += amt;
        else if (t?.category === 'commission') weekMap[key].commissions += amt;
      });
      const revArr = Object.values(weekMap)?.reverse()?.slice(0, 4);
      setRevenueData(revArr?.length > 0 ? revArr : [{ name: 'This Week', rides: 0, deliveries: 0, commissions: 0 }]);

      // Geographic distribution from pickup addresses
      const geoMap = {};
      orders?.forEach(order => {
        const addr = order?.pickup_address || 'Unknown';
        const area = addr?.split(',')?.[0]?.trim() || 'Unknown';
        if (!geoMap?.[area]) geoMap[area] = { name: area, orders: 0, revenue: 0 };
        geoMap[area].orders++;
        geoMap[area].revenue += parseFloat(order?.total_amount || 0);
      });
      const geoArr = Object.values(geoMap)?.sort((a, b) => b?.orders - a?.orders)?.slice(0, 6);
      setGeographicData(geoArr?.length > 0 ? geoArr : [{ name: 'No data', orders: 0, revenue: 0 }]);

    } catch (err) {
      setError(err?.message || 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData, refreshTrigger]);

  const chartOptions = [
    { id: 'bookings', label: 'Booking Trends', icon: 'TrendingUp' },
    { id: 'drivers', label: 'Driver Utilization', icon: 'Users' },
    { id: 'satisfaction', label: 'Customer Satisfaction', icon: 'Star' },
    { id: 'geographic', label: 'Geographic Distribution', icon: 'MapPin' },
    { id: 'revenue', label: 'Revenue Breakdown', icon: 'DollarSign' },
  ];

  const formatCurrency = (value) => `KES ${(value / 1000)?.toFixed(0)}K`;

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-[300px]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    switch (activeChart) {
      case 'bookings':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={bookingTrendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
              <Area type="monotone" dataKey="rides" stackId="1" stroke="#1d4ed8" fill="#dbeafe" name="Rides" />
              <Area type="monotone" dataKey="deliveries" stackId="1" stroke="#d97706" fill="#fef3c7" name="Deliveries" />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'drivers':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={driverUtilizationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
              <Bar dataKey="available" fill="#e2e8f0" name="Total Drivers" />
              <Bar dataKey="active" fill="#1d4ed8" name="Active Drivers" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'satisfaction':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={satisfactionData} cx="50%" cy="50%" innerRadius={60} outerRadius={120} paddingAngle={5} dataKey="value">
                {satisfactionData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'geographic':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={geographicData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" stroke="#64748b" fontSize={12} />
              <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={10} width={90} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
              <Bar dataKey="orders" fill="#1d4ed8" name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'revenue':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} tickFormatter={formatCurrency} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="rides" fill="#38a169" name="Ride Payments" />
              <Bar dataKey="deliveries" fill="#1d4ed8" name="Delivery Payments" />
              <Bar dataKey="commissions" fill="#d97706" name="Commissions" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">Performance Analytics</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchChartData}
            className="p-2 hover:bg-surface-hover rounded-lg transition-colors duration-200"
            title="Refresh charts"
          >
            <Icon name="RefreshCw" size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-error-50 border border-error-200 rounded-lg text-error text-sm">{error}</div>
      )}

      <div className="card">
        {/* Chart Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4">
          {chartOptions?.map((option) => (
            <button
              key={option?.id}
              onClick={() => setActiveChart(option?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                activeChart === option?.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:bg-surface-hover hover:text-primary'
              }`}
            >
              <Icon name={option?.icon} size={16} />
              <span>{option?.label}</span>
            </button>
          ))}
        </div>

        {/* Chart Display */}
        <div className="relative">
          {renderChart()}
        </div>

        {/* Chart Legend/Summary */}
        <div className="mt-4 pt-4 border-t border-border">
          {activeChart === 'bookings' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-text-muted">Total Bookings</p>
                <p className="text-lg font-semibold text-text-primary">{summaryStats?.totalBookings?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Peak Period</p>
                <p className="text-lg font-semibold text-text-primary">{summaryStats?.peakHour}</p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Data Source</p>
                <p className="text-lg font-semibold text-success">Live</p>
              </div>
            </div>
          )}

          {activeChart === 'satisfaction' && (
            <div className="flex flex-wrap justify-center gap-4">
              {satisfactionData?.map((item) => (
                <div key={item?.name} className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                  <span className="text-sm text-text-primary">{item?.name}: {item?.value}%</span>
                </div>
              ))}
            </div>
          )}

          {activeChart === 'revenue' && (
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-success"></div>
                <span className="text-sm text-text-primary">Ride Payments</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-secondary"></div>
                <span className="text-sm text-text-primary">Delivery Payments</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-warning"></div>
                <span className="text-sm text-text-primary">Commissions</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceCharts;